import { useState, useEffect } from "react";

const STORAGE_KEY = "shc_questionnaire_draft";

const OBJECTIVE_OPTIONS = [
  { key: "sale", icon: "🏦", label: "Sale of Business", sub: "Exit to a strategic acquirer, corporate group, or private equity at the highest possible multiple" },
  { key: "collect", icon: "💰", label: "Collect Ongoing Dividends", sub: "Transition clinical load while the practice generates sustainable income through associate billing" },
  { key: "succession", icon: "🤝", label: "Associate Succession", sub: "Transition ownership progressively to associate doctors over time" },
  { key: "expansion", icon: "📍", label: "Network Expansion", sub: "Use the clinic as a platform for multi-site or multi-specialty growth" },
];

const SECTIONS = [
  {
    id: "intro",
    title: "Practice Profile",
    subtitle: "Tell us about your clinic",
    icon: "🏥",
    fields: [
      { id: "clinic_name", label: "Clinic / Practice Name", type: "text", placeholder: "e.g. Riverdale Specialist Clinic" },
      { id: "specialty", label: "Primary Specialty", type: "select", options: ["Allied Health (Physio / OT / Dietetics)", "Audiology", "Bariatric / Weight Loss Surgery", "Cardiology", "Cosmetic / Aesthetic Medicine", "Dental / Orthodontics", "Dermatology", "Endocrinology", "ENT", "Fertility / IVF", "Gastroenterology", "General Surgery", "Gynaecology", "Haematology", "Mental Health / Psychology", "Neurology", "Obstetrics / Maternity", "Oncology", "Ophthalmology", "Respiratory / Thoracic", "Rheumatology", "Sleep Medicine", "Sports Medicine / Orthopaedics", "Urology", "Vascular Surgery", "Other"] },
      { id: "location", label: "Primary Location(s)", type: "text", placeholder: "e.g. Suburb or state" },
      { id: "num_surgeons", label: "Number of Surgeons / Specialists", type: "select", options: ["1", "2-3", "4-6", "7-10", "10+"] },
      { id: "num_support", label: "Number of Support Staff (admin, nursing, allied health)", type: "select", options: ["1-5", "6-10", "11-20", "21-50", "50+"] },
      { id: "annual_revenue", label: "Approximate Annual Billings", type: "select", options: ["Under $1M", "$1M - $3M", "$3M - $5M", "$5M - $10M", "$10M+", "Prefer not to say"] },
      { id: "owner_objective", label: "What is your primary long term objective for the practice?", type: "objectives" },

    ],
  },
  {
    id: "challenges",
    title: "Challenges, Support & Goals",
    subtitle: "Help us understand your pain points, where we can add value, and where you want to take your practice",
    icon: "🎯",
    fields: [
      {
        id: "top_challenges",
        label: "What are your biggest operational challenges right now?",
        type: "multicheck",
        options: [
          "Admin burden / loss of medical work time",
          "Poor systems integration (data does not talk to each other)",
          "Billing errors or revenue leakage",
          "Difficulty attracting or retaining skilled staff",
          "Managing compliance and accreditation requirements",
          "Cash flow management",
          "Lack of financial visibility / reporting",
          "Scaling the practice (capacity constraints)",
          "Managing multiple locations",
          "Referral network growth",
          "Technology is outdated or underperforming",
          "Lack of time to focus on clinical work",
        ],
      },
      {
        id: "support_areas",
        label: "Which areas would you most value external support in?",
        type: "multicheck",
        options: [
          "Financial management & reporting",
          "Systems implementation & upgrade",
          "HR & payroll management",
          "Quality & accreditation support",
          "Business development & marketing",
          "Strategic planning",
          "Practice management & operations",
          "Supplier & procurement negotiation",
          "Recruiting & onboarding staff",
          "IT infrastructure & data security",
          "Data analytics & clinic benchmarking",
          "Patient engagement & Net Promoter Score",
          "Referrer programs & engagement",
          "AI & workflow integration",
          "Succession planning & exit strategy advisory",
        ],
      },
      {
        id: "growth_goals",
        label: "What are your primary goals for the next 3-5 years?",
        type: "multicheck",
        options: [
          "Grow patient volumes",
          "Open additional locations",
          "Reduce personal workload / non-clinical time",
          "Maintain current size but improve profitability",
          "Bring in additional surgeons / specialists",
          "Expand into adjacent services",
          "Improve systems and processes",
          "Spend more time on clinical work",
          "Plan for succession or transition of ownership",
        ],
      },
      { id: "succession", label: "Are you thinking about succession or transition planning?", type: "select", options: ["Yes — actively planning", "Yes — starting to think about it", "Not right now, but open to advice", "No"] },
    ],
  },
  {
    id: "systems",
    title: "Systems & Technology",
    subtitle: "Help us understand your current technology stack",
    icon: "⚙️",
    subsections: [
      {
        label: "Practice Management & Patient",
        fields: [
          { id: "pms", label: "Patient Management System (PMS)", type: "text", placeholder: "e.g. Genie, Best Practice, Medical Director, MedicalDirector Cloud" },
          { id: "booking", label: "Online Booking / Scheduling Tool", type: "text", placeholder: "e.g. HotDoc, HealthEngine, in-house" },
          { id: "telehealth", label: "Telehealth Platform (if used)", type: "text", placeholder: "e.g. Coviu, Zoom, Doxy.me, none" },
        ],
      },
      {
        label: "Quality & Risk",
        fields: [
          { id: "qms", label: "Quality Management System (QMS)", type: "text", placeholder: "e.g. Medi-Q, QIP accreditation tools, internal SOPs, none" },
          { id: "incident", label: "Incident / Risk Reporting System", type: "text", placeholder: "e.g. RiskMan, Datix, manual logs, none" },
          { id: "risk_satisfaction", label: "Confidence in current quality & risk framework", type: "rating", max: 5 },
        ],
      },
      {
        label: "Financial & Billing",
        fields: [
          { id: "accounting", label: "Accounting / Financial System", type: "text", placeholder: "e.g. Xero, MYOB, QuickBooks, manual" },
          { id: "billing", label: "Medical Billing / Claiming Platform", type: "text", placeholder: "e.g. Tyro Health, Eclipse, Medipass, manual" },
        ],
      },
      {
        label: "HR, Payroll & Compliance",
        fields: [
          { id: "payroll", label: "Payroll System", type: "text", placeholder: "e.g. Xero Payroll, MYOB, Employment Hero, external provider" },
          { id: "hr", label: "HR / People Management Platform", type: "text", placeholder: "e.g. Employment Hero, Rippling, spreadsheets, none" },
          { id: "credentialing", label: "Credentialing & Compliance Tracking", type: "text", placeholder: "e.g. Tanda, manual, AHPRA tracking in-house" },
        ],
      },
    ],
  },
  {
    id: "contact",
    title: "Next Steps",
    subtitle: "How should we follow up?",
    icon: "✉️",
    fields: [
      { id: "contact_name", label: "Your Name", type: "text", placeholder: "Dr / Mr / Ms ..." },
      { id: "contact_role", label: "Your Role", type: "text", placeholder: "e.g. Principal Surgeon, Practice Manager" },
      { id: "contact_email", label: "Email Address", type: "email", placeholder: "your@email.com" },
      { id: "contact_phone", label: "Phone Number", type: "text", placeholder: "+61 4xx xxx xxx" },
      { id: "preferred_contact", label: "Preferred Contact Method", type: "select", options: ["Email", "Phone call", "Video call", "In person meeting"] },
      { id: "hear_about", label: "How did you hear about SHC Services?", type: "text", placeholder: "e.g. referral, LinkedIn, conference, direct outreach" },
      { id: "additional_notes", label: "Anything else you would like us to know?", type: "textarea", placeholder: "Open floor — add anything that does not fit above." },
    ],
  },
];

const TOTAL_SECTIONS = SECTIONS.length;

// ── Validation ────────────────────────────────────────────────────────────────
function validateSection(section, answers) {
  const errors = {};
  const allFields = section.fields
    ? section.fields
    : (section.subsections || []).flatMap((s) => s.fields);

  for (const field of allFields) {
    if (!field.required) continue;
    const val = answers[field.id];
    if (field.type === "multicheck" || field.type === "objectives") {
      if (!val || val.length === 0) errors[field.id] = "Please select at least one option.";
    } else if (field.type === "email") {
      if (!val || !val.trim()) {
        errors[field.id] = "This field is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim())) {
        errors[field.id] = "Please enter a valid email address.";
      }
    } else {
      if (!val || (typeof val === "string" && !val.trim())) {
        errors[field.id] = "This field is required.";
      }
    }
  }
  return errors;
}

// ── Submission ────────────────────────────────────────────────────────────────
async function submitAnswers(answers) {
  const WEBHOOK_URL = "https://hook.eu1.make.com/5v2plfyg7vhdndfpzqec43mtqcq6x1ci";

  if (!WEBHOOK_URL) {
    // Dev fallback: log to console and simulate success
    console.log("SHC Questionnaire submission:", JSON.stringify(answers, null, 2));
    await new Promise((r) => setTimeout(r, 1000));
    return { ok: true };
  }

  const res = await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      submitted_at: new Date().toISOString(),
      source: "SHC Practice Needs Assessment",
      answers,
    }),
  });

  return res;
}

// ── Sub-components ────────────────────────────────────────────────────────────
function RatingInput({ id, max, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          onClick={() => onChange(id, n)}
          style={{
            width: "40px", height: "40px", borderRadius: "8px",
            border: value === n ? "2px solid #00C896" : "2px solid #2a2a3a",
            background: value === n ? "#00C896" : "#1a1a2a",
            color: value === n ? "#0a0a14" : "#888",
            fontWeight: "700", fontSize: "14px", cursor: "pointer",
            transition: "all 0.15s", fontFamily: "inherit",
          }}
        >
          {n}
        </button>
      ))}
      <span style={{ color: "#94A3B8", fontSize: "12px", alignSelf: "center", marginLeft: "4px" }}>
        {max === 5 ? (value ? ["", "Poor", "Below avg", "Average", "Good", "Excellent"][value] : "Rate 1–5") : ""}
      </span>
    </div>
  );
}

function ObjectiveCards({ id, value, onChange, error }) {
  const safeValue = value || [];
  const toggle = (key) => {
    const next = safeValue.includes(key)
      ? safeValue.filter((v) => v !== key)
      : [...safeValue, key];
    onChange(id, next);
  };
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "10px" }}>
        {OBJECTIVE_OPTIONS.map((opt) => {
          const selected = safeValue.includes(opt.key);
          return (
            <button
              key={opt.key}
              onClick={() => toggle(opt.key)}
              style={{
                padding: "20px", borderRadius: "12px",
                border: selected ? "2px solid #00A896" : error ? "2px solid #e05555" : "2px solid #FFFFFF",
                background: "#FFFFFF",
                boxShadow: selected ? "0 0 0 2px #00A896" : "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                transition: "all 0.2s", position: "relative",
              }}
            >
              {selected && (
                <div style={{ position: "absolute", top: "12px", right: "12px", width: "20px", height: "20px", borderRadius: "50%", background: "#00C896", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", color: "#FFFFFF", fontWeight: "800" }}>✓</div>
              )}
              <div style={{ fontSize: "26px", marginBottom: "10px" }}>{opt.icon}</div>
              <div style={{ color: "#1B2F4B", fontWeight: "700", fontSize: "14px", marginBottom: "10px" }}>{opt.label}</div>
              <div style={{ color: "#64748B", fontSize: "12px", lineHeight: "1.5" }}>{opt.sub}</div>
            </button>
          );
        })}
      </div>
      {error && <p style={{ color: "#e05555", fontSize: "12px", marginTop: "6px" }}>{error}</p>}
    </>
  );
}

function MultiCheck({ id, options, value, onChange, error }) {
  const safeValue = value || [];
  const toggle = (opt) => {
    const next = safeValue.includes(opt)
      ? safeValue.filter((v) => v !== opt)
      : [...safeValue, opt];
    onChange(id, next);
  };
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
        {options.map((opt) => {
          const selected = safeValue.includes(opt);
          return (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              style={{
                padding: "8px 14px", borderRadius: "6px",
                border: selected ? "1.5px solid #00A896" : error ? "1.5px solid #e05555" : "1.5px solid #E2E8F0",
                background: selected ? "rgba(0,168,150,0.1)" : "#FFFFFF",
                color: selected ? "#00897A" : "#1B2F4B",
                fontSize: "13px", cursor: "pointer", transition: "all 0.15s",
                fontFamily: "inherit", lineHeight: "1.3", textAlign: "left",
              }}
            >
              {selected && <span style={{ marginRight: "5px" }}>✓</span>}{opt}
            </button>
          );
        })}
      </div>
      {error && <p style={{ color: "#e05555", fontSize: "12px", marginTop: "6px" }}>{error}</p>}
    </>
  );
}

function Field({ field, value, onChange, error }) {
  const hasError = !!error;
  const inputStyle = {
    width: "100%", padding: "12px 14px",
    background: "#F0F2F5",
    border: hasError ? "2px solid #e05555" : "2px solid #2a2a3a",
    borderRadius: "8px", color: "#1B2F4B", fontSize: "14px",
    fontFamily: "inherit", outline: "none", transition: "border-color 0.15s",
    boxSizing: "border-box",
  };

  const renderInput = () => {
    switch (field.type) {
      case "text":
      case "email":
        return (
          <input
            type={field.type}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
            style={inputStyle}
          />
        );
      case "select":
        return (
          <select
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
            style={{ ...inputStyle, cursor: "pointer" }}
          >
            <option value="">— Select —</option>
            {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case "textarea":
        return (
          <textarea
            placeholder={field.placeholder}
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
            rows={4}
            style={{ ...inputStyle, resize: "vertical", lineHeight: "1.6" }}
          />
        );
      case "rating":
        return <RatingInput id={field.id} max={field.max} value={value} onChange={onChange} />;
      case "multicheck":
        return <MultiCheck id={field.id} options={field.options} value={value} onChange={onChange} error={error} />;
      case "objectives":
        return <ObjectiveCards id={field.id} value={value} onChange={onChange} error={error} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ marginBottom: "24px" }}>
      <label style={{ display: "block", color: "#1B2F4B", fontSize: "13px", fontWeight: "600", marginBottom: "8px", letterSpacing: "0.03em", textTransform: "uppercase" }}>
        {field.label}
        {field.required && <span style={{ color: "#00C896", marginLeft: "4px" }}>*</span>}
      </label>
      {renderInput()}
      {hasError && field.type !== "multicheck" && field.type !== "objectives" && (
        <p style={{ color: "#e05555", fontSize: "12px", marginTop: "5px" }}>{error}</p>
      )}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Persist to localStorage on every answer change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    } catch { /* storage unavailable */ }
  }, [answers]);

  const handleChange = (id, val) => {
    setAnswers((prev) => ({ ...prev, [id]: val }));
    // Clear error on change
    if (errors[id]) setErrors((prev) => { const e = { ...prev }; delete e[id]; return e; });
  };

  const section = SECTIONS[currentSection];
  const progress = ((currentSection + 1) / TOTAL_SECTIONS) * 100;

  const goForward = async () => {
    const sectionErrors = validateSection(section, answers);
    if (Object.keys(sectionErrors).length > 0) {
      setErrors(sectionErrors);
      // Scroll to first error
      setTimeout(() => {
        const el = document.querySelector("[data-error='true']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setErrors({});

    if (currentSection < TOTAL_SECTIONS - 1) {
      setCurrentSection((p) => p + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Submit
      setSubmitting(true);
      setSubmitError(null);
      try {
        const res = await submitAnswers(answers);
        if (res.ok || res === true) {
          localStorage.removeItem(STORAGE_KEY);
          setSubmitted(true);
        } else {
          setSubmitError("Submission failed. Please try again or contact us directly.");
        }
      } catch (err) {
        setSubmitError("Network error. Please check your connection and try again.");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const goBack = () => {
    setErrors({});
    setCurrentSection((p) => Math.max(0, p - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToSection = (i) => {
    if (i < currentSection) {
      setErrors({});
      setCurrentSection(i);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ── Submitted state ──────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#F4F6F9", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "40px 20px" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@700&display=swap');`}</style>
        <div style={{ textAlign: "center", maxWidth: "520px" }}>
          <div style={{ fontSize: "64px", marginBottom: "24px" }}>✅</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: "#00C896", fontSize: "36px", marginBottom: "16px" }}>Thank you.</h1>
          <p style={{ color: "#64748B", lineHeight: "1.7", fontSize: "16px", marginBottom: "32px" }}>Your responses have been received. The SHC Services team will review your submission and be in touch within 2 business days.</p>
          <div style={{ background: "#F0F2F5", borderRadius: "12px", padding: "24px", border: "1px solid #E2E8F0" }}>
            <p style={{ color: "#94A3B8", fontSize: "13px", margin: 0 }}>Questions? Reach us at</p>
            <p style={{ color: "#00C896", fontWeight: "600", margin: "4px 0 0" }}>contactus@specialisthealthclinics.com</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#F4F6F9", fontFamily: "'DM Sans', sans-serif", color: "#1B2F4B" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@700&display=swap');
        input:focus, select:focus, textarea:focus { border-color: #00C896 !important; outline: none; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #F4F6F9; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
      `}</style>

      {/* Header */}
      <div style={{ background: "#FFFFFF", borderBottom: "1px solid #E2E8F0", padding: "20px 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#00C896" }} />
              <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#00C896" }}>SHC Services</span>
            </div>
            <p style={{ margin: "4px 0 0", color: "#94A3B8", fontSize: "12px" }}>Practice Needs Assessment</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <span style={{ fontSize: "13px", color: "#64748B" }}>{currentSection + 1} / {TOTAL_SECTIONS}</span>
            <div style={{ width: "120px", height: "4px", background: "#E8F0EE", borderRadius: "2px", marginTop: "6px" }}>
              <div style={{ width: `${progress}%`, height: "100%", background: "#00C896", borderRadius: "2px", transition: "width 0.4s ease" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Section nav dots — consolidated to single nav row, supports backward AND forward to visited */}
      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "0px 20px 0", display: "none" }}>
        {SECTIONS.map((s, i) => (
          <div
            key={s.id}
            title={s.title}
            onClick={() => goToSection(i)}
            style={{
              flex: 1, height: "3px", borderRadius: "2px",
              background: i <= currentSection ? "#00C896" : "#2a2a3a",
              opacity: i === currentSection ? 1 : i < currentSection ? 0.7 : 0.3,
              cursor: i < currentSection ? "pointer" : "default",
              transition: "all 0.3s",
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "28px 20px 100px" }}>
        <div style={{ background: "#FFFFFF", border: "1px solid #E8F0EE", borderRadius: "20px", padding: "32px 28px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <div style={{ marginBottom: "24px" }}>
            <p style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#00A896", margin: "0 0 12px" }}>{section.icon} {section.title}</p>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(22px, 4vw, 30px)", color: "#1B2F4B", margin: "0 0 16px", lineHeight: "1.25", fontWeight: "700" }}>{section.subtitle}</h1>
            <div style={{ width: "100%", height: "4px", background: "#E8F0EE", borderRadius: "2px", marginBottom: "6px" }}>
              <div style={{ width: `${progress}%`, height: "100%", background: "#00A896", borderRadius: "2px", transition: "width 0.4s ease" }} />
            </div>
            <p style={{ color: "#94A3B8", fontSize: "11px", margin: "4px 0 0" }}>Fields marked <span style={{ color: "#00A896" }}>*</span> are required.</p>
          </div>
          {section.fields ? (
            section.fields.map((field) => (
              <div key={field.id} data-error={!!errors[field.id] || undefined}>
                <Field field={field} value={answers[field.id]} onChange={handleChange} error={errors[field.id]} />
              </div>
            ))
          ) : (
            section.subsections?.map((sub) => (
              <div key={sub.label} style={{ marginBottom: "36px" }}>
                <div style={{ paddingBottom: "12px", borderBottom: "1px solid #E2E8F0", marginBottom: "20px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: "#00A896" }}>{sub.label}</span>
                </div>
                {sub.fields.map((field) => (
                  <div key={field.id} data-error={!!errors[field.id] || undefined}>
                    <Field field={field} value={answers[field.id]} onChange={handleChange} error={errors[field.id]} />
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Validation summary */}
        {Object.keys(errors).length > 0 && (
          <div style={{ marginTop: "16px", padding: "12px 16px", background: "rgba(224,85,85,0.08)", border: "1px solid rgba(224,85,85,0.3)", borderRadius: "8px" }}>
            <p style={{ color: "#e05555", fontSize: "13px", margin: 0 }}>Please complete the required fields above before continuing.</p>
          </div>
        )}

        {/* Submit error */}
        {submitError && (
          <div style={{ marginTop: "16px", padding: "12px 16px", background: "rgba(224,85,85,0.08)", border: "1px solid rgba(224,85,85,0.3)", borderRadius: "8px" }}>
            <p style={{ color: "#e05555", fontSize: "13px", margin: 0 }}>{submitError}</p>
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "32px" }}>
          <button
            onClick={goBack}
            disabled={currentSection === 0}
            style={{
              padding: "14px 28px", borderRadius: "10px", border: "2px solid #E2E8F0",
              background: "transparent",
              color: currentSection === 0 ? "#CBD5E1" : "#64748B",
              fontSize: "14px", fontWeight: "600",
              cursor: currentSection === 0 ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Back
          </button>

          <button
            onClick={goForward}
            disabled={submitting}
            style={{
              padding: "14px 36px", borderRadius: "10px", border: "none",
              background: submitting ? "#E2F7F3" : "linear-gradient(135deg, #00C896, #00897A)",
              color: submitting ? "#00897A" : "#FFFFFF",
              fontSize: "14px", fontWeight: "700",
              cursor: submitting ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              boxShadow: submitting ? "none" : "0 4px 24px rgba(0,200,150,0.4)",
              transition: "all 0.2s",
            }}
          >
            {submitting ? "Submitting…" : currentSection < TOTAL_SECTIONS - 1 ? "Continue" : "Submit"}
          </button>
        </div>

        <p style={{ textAlign: "center", color: "#CBD5E1", fontSize: "11px", marginTop: "24px" }}>
          {SECTIONS.map((s) => s.title).join("  ·  ")}
        </p>
      </div>
    </div>
  );
}
